import { ChangeDetectorRef, Component, inject, OnInit, signal, ViewChild, TemplateRef, computed, ElementRef, AfterViewChecked, linkedSignal, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import { DropdownComponent, DropdownOption } from "../../../shared/components/ui/dropdown/dropdown.component";
import { ButtonComponent } from "../../../shared/components/ui/button/button.component";
import { ApiService } from '../../../core/services/api.service';
import { PaginatedTableComponent, TableHeader } from "../../../shared/components/tables/paginated-table/paginated-table.component";
import { IconComponent, IconName } from '../../../shared/icons/icon.component';
import { ToasterService } from '../../../core/services/toaster.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationModalComponent, ConfirmationModalData } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { LoaderService } from '../../../core/services/loader.service';
import { SlidePanelComponent } from '../../../shared/components/slide-panel/slide-panel.component';
import { InputComponent } from '../../../shared/components/ui/input/input.component';
import { AuditLogPanelComponent } from '../../../shared/components/audit-log-panel/audit-log-panel.component';
import axios from 'axios';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuditService } from '../../../core/services/audit.service';
import { AuditAction, AuditEntityType } from '../../../core/models/audit-log.model';
import { FOR_ENV_BOTH, FOR_ENV_PRODUCTION, FOR_ENV_TEST } from '../deck-admin/for-env.util';

interface DocumentFile {
  id: string;
  file_name: string;
  original_name: string;
  description?: string;
  file_size: number;
  mime_type: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  uploaded_by?: string;
  status: 'EMBEDDED' | 'FAILED' | 'PENDING' | 'PROCESSED' | 'DELETED';
  collection_name: string;
  product_name: string;
  feature_name: string;
  feature_option_name: string;
}

interface Collection {
  id: string;
  collection_name: string;
  description?: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  product_id: number;
  product_feature_id?: number;
  feature_option_id?: number;
  document_count?: number;
  name?: string;
  /** Where the collection is visible: 1 => live, 2 => test, 3 => both. */
  for_env?: number;
}

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  time: string;
  timestamp: Date;
  isLoading?: boolean;
  sources?: { name: string, id: string }[];
}

@Component({
  selector: 'app-knowledge-base',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    DropdownComponent,
    ButtonComponent,
    PaginatedTableComponent,
    SlidePanelComponent,
    InputComponent,
    IconComponent,
    MarkdownModule,
    DatePipe,
    AuditLogPanelComponent
  ],
  providers: [provideMarkdown()],
  standalone: true,
  templateUrl: './knowledge-base.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    /* Markdown Content Styling */
    ::ng-deep .markdown-content {
      color: inherit;
      
      h1, h2, h3, h4, h5, h6 {
        font-weight: 600;
        margin-top: 1em;
        margin-bottom: 0.5em;
        color: #1E1B4B;
      }
      h1 { font-size: 1.25rem; }
      h2 { font-size: 1.125rem; }
      h3 { font-size: 1rem; }
      
      .dark & {
        h1, h2, h3, h4, h5, h6 { color: #BAE6FD; }
      }

      p { margin-bottom: 0.75rem; line-height: 1.6; }
      p:last-child { margin-bottom: 0; }

      ul, ol { margin: 0.5rem 0; padding-left: 1.5rem; }
      ul { list-style-type: disc; }
      ol { list-style-type: decimal; }
      li { margin-bottom: 0.25rem; }

      strong { font-weight: 600; }
      
      code {
        background: rgba(0,0,0,0.05);
        padding: 0.2rem 0.4rem;
        border-radius: 4px;
        font-family: monospace;
        font-size: 0.9em;
      }

      .dark & code { background: rgba(255,255,255,0.1); }

      table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.875rem; }
      th, td { border: 1px solid rgba(0,0,0,0.1); padding: 0.5rem; text-align: left; }
      th { background: rgba(0,0,0,0.02); font-weight: 600; }
      
      .dark & th, .dark & td { border-color: rgba(255,255,255,0.1); }
      .dark & th { background: rgba(255,255,255,0.05); }

      hr { border: 0; border-top: 1px solid rgba(0,0,0,0.1); margin: 1rem 0; }
      .dark & hr { border-top-color: rgba(255,255,255,0.1); }
    }
  `]
})
export class KnowledgeBaseComponent implements OnInit, AfterViewChecked {
  @ViewChild('collectionDetailTemplate') collectionDetailTemplate!: TemplateRef<any>;
  @ViewChild('documentDetailTemplate') documentDetailTemplate!: TemplateRef<any>;
  @ViewChild('chatScrollArea') chatScrollArea!: ElementRef<HTMLDivElement>;
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;
  private fb = inject(FormBuilder);
  loaderService = inject(LoaderService);
  private toasterService = inject(ToasterService);
  private dialog = inject(MatDialog);
  private apiservice = inject(ApiService);
  private auditService = inject(AuditService);
  router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  ENV_PRODUCTION: Boolean = environment.production;
  // prism-new serves RAG natively at `/rag` on the prism session; ApiService routes the relative
  // path there (prism-new-endpoints.ts) and maps bodies back to the gateway's.
  private baseURL: string = environment.prismNew.enabled
    ? 'rag'
    : this.ENV_PRODUCTION
    ? 'https://prism.bridgingtech.com/gateway/api/v1/rag'
    // Local prism-microservices gateway (npm run start:all:dev in prism-microservices)
    : 'http://localhost:3050/api/v1/rag';
    // : 'https://testprism.bridgingtech.com/gateway/api/v1/rag';

  // States
  panelOpen = signal(false);
  documentPanelOpen = signal(false);
  queryPanelOpen = signal(false);
  selectedFeature = signal<string | null>(null);
  selectedOption = signal<string | null>(null);
  selectedCollection = signal<Collection | null>(null);
  selectedDocument = signal<DocumentFile | null>(null);
  dialogRef = signal<MatDialogRef<any> | null>(null);
  isLoading = signal(false);
  formSubmitted = signal(false);
  errorMessage = signal('');
  auditEntityTypes = [AuditEntityType.KNOWLEDGE_BASE];

  // Query Panel State
  queryText = signal('');
  queryCollectionName = signal<string>('');
  isQueryCollectionFrozen = signal(false);
  isQueryLoading = signal(false);
  querySubmitted = signal(false);
  chatHistory = signal<ChatMessage[]>([]);

  querySuggestions: string[] = [
    'What are the key topics covered in this collection?',
    'Summarize the most important documents.',
    'What policies or procedures are described here?'
  ];

  // Stats
  totalCollections = signal(0);
  totalDocuments = signal(0);
  embeddedDocuments = signal(0);
  failedDocuments = signal(0);

  // Collection Data
  page = signal(1);
  pageSize = signal(10);
  collectionData = signal<Collection[]>([]);
  productOptions = signal<DropdownOption[]>([]);
  rawFeatureOptions = signal<DropdownOption[]>([]);
  rawFeatureOptionList = signal<DropdownOption[]>([]);

  // Document Data
  docPage = signal(1);
  docPageSize = signal(10);
  documentData = signal<DocumentFile[]>([]);
  selectedFile: File | null = null;

  // Active vs Archived document view.
  // Archiving is a soft-delete (backend sets is_deleted=1, status='DELETED'); archived
  // documents remain listable via GET /documents?status=DELETED and can be restored by an admin.
  documentView = signal<'active' | 'archived'>('active');

  // Search
  collectionSearchQuery = signal('');
  documentSearchQuery = signal('');
  documentStatusFilter = signal('All Status');
  globalFilterProductId = signal<string | null>(null);
  globalFilterFeatureId = signal<string | null>(null);
  globalFeatureOptions = signal<DropdownOption[]>([]);

  // Applied Search (Only update on Apply Filters)
  appliedCollectionSearchQuery = signal('');
  appliedDocumentSearchQuery = signal('');
  appliedStatusFilter = signal('All Status');
  appliedProductId = signal<string | null>(null);
  appliedFeatureId = signal<string | null>(null);

  // Lookup Stores for Mapping
  allProducts = signal<any[]>([]);
  allFeatures = signal<any[]>([]);
  allOptions = signal<any[]>([]);

  featureOptions = computed(() => {
    return [
      ...this.rawFeatureOptions(),
      { value: 'addNewFeature', label: '+ Add New Feature' }
    ];
  });

  featureOptionList = computed(() => {
    return [
      ...this.rawFeatureOptionList(),
      { value: 'addNewOption', label: '+ Add New Option' }
    ];
  });

  collectionOptions = computed(() => {
    return this.collectionData().map(c => ({
      value: String(c.id),
      label: c.collection_name
    }));
  });

  processedCollectionData = computed(() => {
    const collections = this.collectionData();
    const products = this.allProducts();
    const features = this.allFeatures();
    const options = this.allOptions();

    const productId = this.appliedProductId();
    const featureId = this.appliedFeatureId();
    const query = this.appliedCollectionSearchQuery().toLowerCase();

    return collections
      .filter(c => {
        const matchesQuery = c.collection_name.toLowerCase().includes(query);
        const matchesProduct = !productId || String(c.product_id) === String(productId);
        const matchesFeature = !featureId || String(c.product_feature_id) === String(featureId);
        return matchesQuery && matchesProduct && matchesFeature;
      })
      .map(c => {
        const product = products.find(p => p.id == c.product_id);
        const feature = features.find(f => f.id == c.product_feature_id);
        const option = options.find(o => o.id == c.feature_option_id);

        return {
          ...c,
          product_name: product ? (product.product_name || product.name) : 'N/A',
          feature_name: feature ? (feature.feature_name || feature.name) : 'N/A',
          option_name: option ? (option.feature_option_name || option.name) : null,
          document_count: (c as any).document_count || 0
        };
      });
  });

  formatMessage(text: string): string {
    if (!text) return '';
    return text.replace(/\\n/g, '\n');
  }

  // Forms
  form: FormGroup;
  documentForm: FormGroup;

  // Table Headers
  documentTableHeaders: TableHeader[] = [
    { label: 'Document Name', key: 'formatted_name', align: 'text-left', isHtml: true },
    { label: 'Context', key: 'formatted_context', align: 'text-left', isHtml: true },
    { label: 'Status', key: 'formatted_status', align: 'text-left', isHtml: true },
    { label: 'Uploaded Date', key: 'uploaded_at_display', align: 'text-left', isHtml: true }
  ];

  tableActionOptions = [
    { label: 'Delete', value: 'delete', iconName: 'trash' as IconName, isShow: true, visibilityKey: 'isNotDeleted', color: '#EF4444' }
  ];

  // Active view: view, download, and Archive (soft-delete) — replaces the old destructive Delete.
  documentActionOptions = [
    { label: 'View', value: 'view', iconName: 'eye' as IconName, isShow: true, color: '#6366F1' },
    { label: 'Download', value: 'download', iconName: 'download' as IconName, isShow: true, color: '#10B981' },
    { label: 'Archive', value: 'archive', iconName: 'archive' as IconName, isShow: true, color: '#F59E0B' }
  ];

  // Archived view: View (works off the row's s3_url directly) and Restore (back to active state).
  archivedActionOptions = [
    { label: 'View', value: 'view', iconName: 'eye' as IconName, isShow: true, color: '#6366F1' },
    { label: 'Restore', value: 'restore', iconName: 'rotateCcw' as IconName, isShow: true, color: '#10B981' }
  ];

  currentActionOptions = computed(() =>
    this.documentView() === 'archived' ? this.archivedActionOptions : this.documentActionOptions
  );

  /** Environment selector choices; values are `kb_collections.for_env` as strings. */
  envOptions: DropdownOption[] = [
    {
      label: 'Live', shortLabel: 'Environment: Live', value: String(FOR_ENV_PRODUCTION),
      icon: 'globe', iconClass: 'text-emerald-500',
      description: 'Production only', tooltip: 'Visible on prism (production) only'
    },
    {
      label: 'Test', shortLabel: 'Environment: Test', value: String(FOR_ENV_TEST),
      icon: 'flask-conical', iconClass: 'text-amber-500',
      description: 'Test only', tooltip: 'Visible on testprism only'
    },
    {
      label: 'Both', shortLabel: 'Environment: Both', value: String(FOR_ENV_BOTH),
      icon: 'layers', iconClass: 'text-purple-500',
      description: 'Live + Test', tooltip: 'Visible on both prism and testprism'
    }
  ];
  /** Selector value; follows the selected collection, snaps back on cancel. */
  envDraft = linkedSignal(() => String(this.selectedCollection()?.for_env ?? ''));

  statusOptions: DropdownOption[] = [
    { label: 'All Status', value: 'All Status' },
    { label: 'Embedded', value: 'EMBEDDED' },
    { label: 'Failed', value: 'FAILED' },
    { label: 'Pending', value: 'PENDING' }
  ];


  constructor() {
    this.form = this.fb.group({
      collection_name: ['', Validators.required],
      description: [''],
      product_id: ['', Validators.required],
      product_feature_id: ['', Validators.required],
      feature_option_id: ['', Validators.required],
      featureName: [''],
      optionName: [''],
    });

    this.documentForm = this.fb.group({
      collection_id: ['', Validators.required],
      file: [null, Validators.required],
      description: ['']
    });
  }

  get selectedUploadCollectionDetails() {
    const colId = this.documentForm.get('collection_id')?.value;
    if (!colId || colId === 'null' || colId === 'undefined') return null;
    return this.processedCollectionData().find(c => String(c.id) === String(colId));
  }

  get selectedQueryCollectionDetails() {
    const colId = this.queryCollectionName();
    if (!colId || colId === 'null' || colId === 'undefined') return null;
    return this.processedCollectionData().find(c => String(c.id) === String(colId));
  }

  processedDocumentData = computed(() => {
    return this.documentData().map(item => {
      const status = item.status || 'PENDING';
      const isArchivedRow = status === 'DELETED';
      const statusLabel = isArchivedRow ? 'Archived' : (status.charAt(0) + status.slice(1).toLowerCase());
      const statusColor = status === 'EMBEDDED' ? 'text-green-600 bg-green-50/50 border-green-100 dark:bg-green-900/10 dark:border-green-800/50' :
        status === 'FAILED' ? 'text-red-600 bg-red-50/50 border-red-100 dark:bg-red-900/10 dark:border-red-800/50' :
          isArchivedRow ? 'text-slate-500 bg-slate-100/60 border-slate-200 dark:text-slate-400 dark:bg-slate-800/40 dark:border-slate-700' :
          'text-amber-600 bg-amber-50/50 border-amber-100 dark:bg-amber-900/10 dark:border-amber-800/50';

      const contextProduct = item.product_name || 'N/A';
      const contextFeature = item.feature_name || 'N/A';
      const contextOption = item.feature_option_name || 'N/A';

      return {
        ...item,
        formatted_name: `<div class="flex items-center gap-2"><span class="text-purple-600 italic">📄</span> <span class="font-medium text-slate-900 dark:text-white">${item.file_name}</span></div>`,
        formatted_context: `<span class="text-sm text-slate-500 dark:text-slate-400">${contextProduct} > ${contextFeature} > ${contextOption}</span>`,
        formatted_status: `
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium ${statusColor}">
            <span class="w-1.5 h-1.5 rounded-full mr-1.5 ${status === 'EMBEDDED' ? 'bg-green-600' : status === 'FAILED' ? 'bg-red-600' : isArchivedRow ? 'bg-slate-400' : 'bg-amber-600'}"></span>
            ${statusLabel}
          </span>
        `,
        uploaded_at_display: `
          <span class="text-sm text-slate-500 dark:text-slate-400">
            ${new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        `,
        isDeleted: !!item.deleted_at,
        isNotDeleted: !item.deleted_at
      };
    });
  });

  ngOnInit() {
    this.initialDataLoad();
  }

  initialDataLoad() {
    this.loadProducts();
    this.loadAllLookups();
    this.fetchCollections();
    this.fetchDocuments();
    // In a real app, you might have a dedicated stats endpoint.
    // Here we'll rely on the totals returned by list endpoints.
  }

  loadAllLookups() {
    this.apiservice.get<any[]>(`${this.baseURL}/products`, {}, { withCredentials: true }).subscribe(data => this.allProducts.set(data));
    this.apiservice.get<any[]>(`${this.baseURL}/features`, {}, { withCredentials: true }).subscribe(data => this.allFeatures.set(data));
    this.apiservice.get<any[]>(`${this.baseURL}/feature-options`, {}, { withCredentials: true }).subscribe(data => this.allOptions.set(data));
  }

  loadProducts() {
    this.apiservice.get<any[]>(`${this.baseURL}/products`, {}, { withCredentials: true }).subscribe({
      next: (products) => {
        this.productOptions.set(
          products.map((p: any) => ({
            value: String(p.id),
            label: p.product_name || p.name || p.displayName || p.label,
          }))
        );
      },
    });
  }

  onGlobalProductChange(productId: any) {
    this.globalFilterProductId.set(productId);
    this.globalFilterFeatureId.set(null);
    this.globalFeatureOptions.set([]);

    if (!productId) return;

    this.apiservice.get<any[]>(`${this.baseURL}/features`, { product_id: productId }, { withCredentials: true }).subscribe({
      next: (features) => {
        this.globalFeatureOptions.set(
          features.map((f: any) => ({
            value: String(f.product_feature_id ?? f.id),
            label: f.feature_name || f.name || f.displayName || f.label,
          }))
        );
      },
    });
  }

  onGlobalFeatureChange(featureId: any) {
    this.globalFilterFeatureId.set(featureId);
  }

  applyGlobalFilters() {
    this.appliedCollectionSearchQuery.set(this.collectionSearchQuery());
    this.appliedDocumentSearchQuery.set(this.documentSearchQuery());
    this.appliedStatusFilter.set(this.documentStatusFilter());
    this.appliedProductId.set(this.globalFilterProductId());
    this.appliedFeatureId.set(this.globalFilterFeatureId());

    this.docPage.set(1);
    this.fetchCollections();
    this.fetchDocuments();

    // Re-check selection after filtering
    const filtered = this.processedCollectionData();
    if (filtered.length > 0 && !filtered.find(c => c.id === this.selectedCollection()?.id)) {
      this.selectedCollection.set(filtered[0]);
    } else if (filtered.length === 0) {
      this.selectedCollection.set(null);
    }
  }

  clearGlobalFilters() {
    this.documentSearchQuery.set('');
    this.documentStatusFilter.set('All Status');
    this.globalFilterProductId.set(null);
    this.globalFilterFeatureId.set(null);
    this.globalFeatureOptions.set([]);

    this.appliedCollectionSearchQuery.set('');
    this.appliedDocumentSearchQuery.set('');
    this.appliedStatusFilter.set('All Status');
    this.appliedProductId.set(null);
    this.appliedFeatureId.set(null);

    this.docPage.set(1);
    this.fetchCollections();
    this.fetchDocuments();
  }

  onProductChange(productId: any) {
    if (!productId) {
      this.rawFeatureOptions.set([]);
      this.rawFeatureOptionList.set([]);
      this.form.patchValue({ product_feature_id: null, feature_option_id: null });
      return;
    }

    this.apiservice.get<any[]>(`${this.baseURL}/features`, { product_id: String(productId) }, { withCredentials: true }).subscribe({
      next: (features) => {
        this.rawFeatureOptions.set(
          features.map((f: any) => ({
            value: String(f.id),
            label: f.feature_name || f.name || f.displayName || f.label,
          }))
        );
      },
    });
    this.form.patchValue({ product_feature_id: null, feature_option_id: null, featureName: '', optionName: '' });
    this.selectedFeature.set(null);
    this.selectedOption.set(null);
  }

  onFeatureChange(featureId: any) {
    this.selectedFeature.set(featureId);

    const featureNameControl = this.form.get('featureName');
    const optionIdControl = this.form.get('feature_option_id');

    if (featureId === 'addNewFeature') {
      featureNameControl?.setValidators([Validators.required]);
    } else {
      featureNameControl?.clearValidators();
    }

    if (featureId) {
      optionIdControl?.setValidators([Validators.required]);
    } else {
      optionIdControl?.clearValidators();
    }
    featureNameControl?.updateValueAndValidity();
    optionIdControl?.updateValueAndValidity();

    if (!featureId || featureId === 'addNewFeature') {
      this.rawFeatureOptionList.set([]);
      this.form.patchValue({ feature_option_id: null, featureName: '', optionName: '' });
      this.selectedOption.set(null);
      return;
    }

    this.apiservice.get<any[]>(`${this.baseURL}/feature-options`, { product_feature_id: String(featureId) }, { withCredentials: true }).subscribe({
      next: (options) => {
        this.rawFeatureOptionList.set(
          options.map((o: any) => ({
            value: String(o.id),
            label: o.option_name || o.name || o.displayName || o.label,
          }))
        );
      },
    });
    this.form.patchValue({ feature_option_id: null, optionName: '' });
    this.selectedOption.set(null);
  }

  onOptionChange(optionId: any) {
    this.selectedOption.set(optionId);
    const optionNameControl = this.form.get('optionName');
    if (optionId === 'addNewOption') {
      optionNameControl?.setValidators([Validators.required]);
    } else {
      optionNameControl?.clearValidators();
    }
    optionNameControl?.updateValueAndValidity();
  }

  // Collection Methods
  openPanel() {
    this.form.reset();
    this.selectedFeature.set(null);
    this.selectedOption.set(null);
    this.rawFeatureOptions.set([]);
    this.rawFeatureOptionList.set([]);
    this.panelOpen.set(true);
  }

  closePanel() {
    this.panelOpen.set(false);
    this.formSubmitted.set(false);
    this.form.reset();
    this.selectedFeature.set(null);
    this.selectedOption.set(null);
    this.rawFeatureOptions.set([]);
    this.rawFeatureOptionList.set([]);
  }

  selectCollection(collection: Collection) {
    this.selectedCollection.set(collection);
    this.docPage.set(1);
    this.fetchDocuments();
  }

  setDocumentView(view: 'active' | 'archived') {
    if (this.documentView() === view) return;
    this.documentView.set(view);
    this.docPage.set(1);
    this.fetchDocuments();
  }

  fetchCollections(): void {
    this.isLoading.set(true);
    const params: any = {
      page: this.page(),
      limit: 100, // Fetch more for sidebar
      product_id: this.appliedProductId(),
      product_feature_id: this.appliedFeatureId(),
      search: this.appliedCollectionSearchQuery()
    };

    if (!params.product_id) delete params.product_id;
    if (!params.product_feature_id) delete params.product_feature_id;
    if (!params.search) delete params.search;

    this.apiservice.get(this.baseURL + '/collections', params, {
      showLoader: true,
      showErrorToaster: true,
      showSuccessToaster: false,
      withCredentials: true,
    }).subscribe({
      next: (response: any) => {
        const data = Array.isArray(response) ? response : (response.data || []);
        this.collectionData.set(data);
        this.totalCollections.set(data.length);
        if (data.length > 0 && !this.selectedCollection()) {
          this.selectedCollection.set(data[0]);
          this.fetchDocuments();
        }
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.toasterService.error(error?.error || error?.message || 'Failed to load collections', 'Collections', 2000);
      }
    });
  }

  submitForm() {
    this.formSubmitted.set(true);
    if (this.form.invalid) {
      console.warn('Form is invalid:', this.form.errors, this.form.value);
      return;
    }

    this.isLoading.set(true);
    const formValue = this.form.getRawValue();

    // Helper to finalize collection creation
    const finalizeCollection = (fId: number | null, oId: number | null) => {
      const payload: any = {
        name: formValue.collection_name,
        description: formValue.description || null,
        product_id: Number(formValue.product_id),
        product_feature_id: fId,
        feature_option_id: oId,
        feature_name: formValue.product_feature_id === 'addNewFeature' ? formValue.featureName : null,
        option_name: formValue.feature_option_id === 'addNewOption' ? formValue.optionName : null,
      };

      // Clean payload
      if (!payload.product_feature_id) delete payload.product_feature_id;
      if (!payload.feature_option_id) delete payload.feature_option_id;
      if (!payload.feature_name) delete payload.feature_name;
      if (!payload.option_name) delete payload.option_name;
      if (!payload.description) delete payload.description;

      this.apiservice.post(`${this.baseURL}/collections`, payload, { withCredentials: true }).subscribe({
        next: (response: any) => {
          this.isLoading.set(false);
          const createdProduct = this.allProducts().find(p => String(p.id) === String(payload.product_id));
          const productName = createdProduct?.product_name || createdProduct?.name || payload.product_id;
          const productKey = createdProduct?.keyname;

          this.auditService.createAuditLog({
            entityType: AuditEntityType.KNOWLEDGE_BASE,
            action: AuditAction.CREATE,
            actionTitle: `Knowledge Base Collection Created`,
            oldValue: null,
            newValue: { ...payload, id: response?.data?.id || response?.id },
            productKey,
            note: `Created collection '${payload.name}' and product '${productName}'`
          }).subscribe();

          this.toasterService.success(response?.data?.message || 'Collection saved successfully', 'Success');
          this.closePanel();
          this.loadAllLookups();
          this.fetchCollections();
          this.auditLogPanel?.reloadLogs();
        },
        error: (err) => {
          this.isLoading.set(false);
          this.toasterService.error(err?.message || 'Error saving collection', 'Error');
        }
      });
    };

    // Step 2: Create Option (if needed)
    const handleOptionStep = (featureId: number | null) => {
      if (formValue.feature_option_id === 'addNewOption' && featureId) {
        const optionPayload = {
          name: formValue.optionName,
          feature_id: Number(featureId)
        };
        this.apiservice.post(`${this.baseURL}/feature-options`, optionPayload, { withCredentials: true }).subscribe({
          next: (res: any) => {
            const newOId = res?.data?.id || res?.id || (res?.data && res.data[0]?.id);
            this.auditService.createAuditLog({
              entityType: AuditEntityType.KNOWLEDGE_BASE,
              action: AuditAction.CREATE,
              actionTitle: `Knowledge Base Option Created`,
              oldValue: null,
              newValue: { id: newOId, name: optionPayload.name, feature_id: optionPayload.feature_id },
              note: `Created feature option '${optionPayload.name}' via Knowledge Base`
            }).subscribe();
            finalizeCollection(featureId, newOId ? Number(newOId) : null);
          },
          error: (err) => {
            this.isLoading.set(false);
            this.toasterService.error(err?.message || 'Error creating new option', 'Error');
          }
        });
      } else {
        const existingOId = (formValue.feature_option_id && formValue.feature_option_id !== 'addNewOption') ? Number(formValue.feature_option_id) : null;
        finalizeCollection(featureId, existingOId);
      }
    };

    // Step 1: Create Feature (if needed)
    if (formValue.product_feature_id === 'addNewFeature') {
      const featurePayload = {
        name: formValue.featureName,
        product_ids: [Number(formValue.product_id)]
      };
      this.apiservice.post(`${this.baseURL}/features`, featurePayload, { withCredentials: true }).subscribe({
        next: (res: any) => {
          const newFId = res?.data?.id || res?.id || (res?.data && res.data[0]?.id);
          if (newFId) {
            this.auditService.createAuditLog({
              entityType: AuditEntityType.KNOWLEDGE_BASE,
              action: AuditAction.CREATE,
              actionTitle: `Knowledge Base Feature Created`,
              oldValue: null,
              newValue: { id: newFId, name: featurePayload.name, product_ids: featurePayload.product_ids },
              note: `Created feature '${featurePayload.name}' via Knowledge Base`
            }).subscribe();
            handleOptionStep(Number(newFId));
          } else {
            this.isLoading.set(false);
            this.toasterService.error('Failed to retrieve new feature ID', 'Error');
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          this.toasterService.error(err?.message || 'Error creating new feature', 'Error');
        }
      });
    } else {
      const existingFId = (formValue.product_feature_id && formValue.product_feature_id !== 'addNewFeature') ? Number(formValue.product_feature_id) : null;
      handleOptionStep(existingFId);
    }
  }

  // Document Methods
  openAddDocumentPanel() {
    this.documentForm.reset();
    if (this.selectedCollection()) {
      this.documentForm.patchValue({ collection_id: String(this.selectedCollection()?.id) });
      this.documentForm.get('collection_id')?.disable();
    }
    this.selectedFile = null;
    this.documentPanelOpen.set(true);
    this.formSubmitted.set(false);
  }

  closeDocumentPanel() {
    this.documentPanelOpen.set(false);
    this.formSubmitted.set(false);
    this.documentForm.get('collection_id')?.enable();
    this.documentForm.reset();
    this.selectedFile = null;
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      this.documentForm.patchValue({ file: file });
      this.cdr.markForCheck();
    }
  }

  removeFile() {
    this.selectedFile = null;
    this.cdr.markForCheck();
  }

  async submitDocumentForm() {
    this.formSubmitted.set(true);
    if (this.documentForm.invalid || !this.selectedFile) return;

    this.isLoading.set(true);
    const formData = new FormData();
    const formValue = this.documentForm.getRawValue();
    const coll = this.collectionData().find(c => String(c.id) === String(formValue.collection_id));

    if (coll) {
      formData.append('product_id', String(coll.product_id));
      if (coll.product_feature_id) formData.append('product_feature_id', String(coll.product_feature_id));
      if (coll.feature_option_id) formData.append('feature_option_id', String(coll.feature_option_id));
    }

    formData.append('file', this.selectedFile);
    if (formValue.description) {
      formData.append('description', formValue.description);
    }

    const token = this.apiservice.getAccessToken();

    try {
      const docData: any = environment.prismNew.enabled
        ? await firstValueFrom(this.apiservice.post(`${this.baseURL}/documents/upload`, formData, { showErrorToaster: false }))
        : await axios({
          method: 'post',
          url: `${this.baseURL}/documents/upload`,
          data: formData,
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        }).then((response) => response.data?.data || response.data);

      this.isLoading.set(false);

      const uploadedProduct = this.allProducts().find(p => String(p.id) === String(coll?.product_id));
      const uploadProductKey = uploadedProduct?.keyname;
      this.auditService.createAuditLog({
        entityType: AuditEntityType.KNOWLEDGE_BASE,
        action: AuditAction.CREATE,
        actionTitle: `Knowledge Base Document Uploaded`,
        oldValue: null,
        newValue: {
          id: docData?.id,
          file_name: this.selectedFile?.name,
          collection_name: coll?.collection_name,
          product_id: coll?.product_id
        },
        productKey: uploadProductKey,
        note: `Uploaded document '${this.selectedFile?.name}' to collection '${coll?.name} and product '${uploadedProduct?.name}'`
      }).subscribe();

      this.toasterService.success('Document uploaded successfully', 'Success');
      this.closeDocumentPanel();
      this.fetchDocuments();
      this.auditLogPanel?.reloadLogs();
    } catch (err: any) {
      this.isLoading.set(false);
      this.toasterService.error('Error uploading document', 'Error');
    }
  }

  /** prism-new lists documents without the gateway's joined catalogue names; fill them from the lookups. */
  private withCatalogNames(doc: any): any {
    if (doc.product_name !== undefined) return doc;
    const option = this.allOptions().find((o) => String(o.id) === String(doc.feature_option_id));
    return {
      ...doc,
      product_name: this.allProducts().find((p) => String(p.id) === String(doc.product_id))?.name,
      feature_name: this.allFeatures().find((f) => String(f.id) === String(option?.feature_id))?.name,
      feature_option_name: option?.name,
    };
  }

  fetchDocuments(): void {
    this.isLoading.set(true);
    const isArchived = this.documentView() === 'archived';
    const params: any = {
      collection_name: this.selectedCollection()?.collection_name,
      search: this.appliedDocumentSearchQuery() || this.appliedCollectionSearchQuery(),
      // Archived view lists soft-deleted documents (status=DELETED); the status dropdown is ignored there.
      status: isArchived ? 'DELETED' : (this.appliedStatusFilter() === 'All Status' ? null : this.appliedStatusFilter()),
      product_id: this.appliedProductId(),
      product_feature_id: this.appliedFeatureId(),
    };

    if (!params.status) delete params.status;
    if (!params.product_id) delete params.product_id;
    if (!params.product_feature_id) delete params.product_feature_id;

    // Only include collection_name when searching within documents specifically
    if (!this.appliedDocumentSearchQuery()) delete params.collection_name;


    this.apiservice.get(this.baseURL + '/documents', params, {
      showLoader: true,
      showErrorToaster: true,
      showSuccessToaster: false,
      withCredentials: true,
    }).subscribe({
      next: (response: any) => {
        let docs = (Array.isArray(response) ? response : (response.data || [])).map((d: any) => this.withCatalogNames(d));

        // Ensure we only show documents for the selected collection
        const selectedColl = this.selectedCollection();
        if (selectedColl) {
          docs = docs.filter((d: any) => d.collection_name === selectedColl.collection_name);

          // Synchronize the sidebar count from the active document list only.
          if (this.documentView() !== 'archived') {
            this.collectionData.update(collections =>
              collections.map(c => c.id === selectedColl.id ? { ...c, document_count: docs.length } : c)
            );
          }
        }

        this.documentData.set(docs);

        // KPI cards stay scoped to active documents; the Archived view is a browsing surface only.
        if (this.documentView() !== 'archived') {
          this.totalDocuments.set(docs.length);
          this.embeddedDocuments.set(docs.filter((d: any) => d.status === 'EMBEDDED').length);
          this.failedDocuments.set(docs.filter((d: any) => d.status === 'FAILED').length);
        }

        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.documentData.set([]);
      }
    });
  }

  onDocumentPageChange(page: number): void {
    this.docPage.set(page);
  }

  handleViewDocument(row: any) {
    const url = row.s3_url || row.source_url;
    if (url) {
      window.open(url, '_blank');
    } else {
      // Fallback: trigger download if no direct URL
      this.handleDownload(row);
    }
  }

  handleDownload(row: any) {
    if (!row.id) {
      this.toasterService.warning('No document ID available.', 'Warning');
      return;
    }

    this.apiservice.get(this.baseURL + `/documents/${row.id}/presigned-url`, {}, {
      //showLoader: true,
      showErrorToaster: true,
      showSuccessToaster: false,
      withCredentials: true,
    }).subscribe({
      next: (response: any) => {
        const presignedUrl = response.data?.url || response?.url;
        if (!presignedUrl) {
          this.toasterService.warning('No download link returned from server.', 'Warning');
          return;
        }
        window.open(presignedUrl, '_blank');
      },
      error: (error: any) => {
        console.error('Download error:', error);
      }
    });
  }

  handleArchiveDocument(id: string) {
    const document = this.documentData().find(d => String(d.id) === String(id));
    const safeName = (document?.file_name || 'this document').replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    this.dialog.open(ConfirmationModalComponent, {
      width: '440px',
      data: {
        title: 'Archive document',
        description: `Archive "${document?.file_name || 'this document'}"?`,
        descriptionHtml: `Archive <strong>${safeName}</strong>? It will be removed from active use and hidden from search, but <strong>nothing is permanently deleted</strong> — you can restore it anytime from the Archived view.`,
        confirmLabel: 'Archive',
        cancelLabel: 'Cancel',
        variant: 'primary'
      } as ConfirmationModalData
    }).afterClosed().subscribe(res => {
      if (res) {
        // Backend DELETE is a soft-delete (is_deleted=1, status='DELETED'); the document is recoverable.
        this.apiservice.delete(this.baseURL + `/documents/${id}`, { withCredentials: true }).subscribe(() => {
          const docProduct = this.allProducts().find(p => p.product_name === document?.product_name || p.name === document?.product_name);
          const docProductKey = docProduct?.keyname;
          // Reload the audit trail only AFTER the log has been persisted, otherwise the
          // panel refreshes before the create completes and the new entry appears "missing".
          this.auditService.createAuditLog({
            entityType: AuditEntityType.KNOWLEDGE_BASE,
            action: AuditAction.DELETE,
            actionTitle: `Knowledge Base Document Archived`,
            oldValue: document,
            newValue: null,
            productKey: docProductKey,
            note: `Archived document '${document?.file_name}' from collection '${document?.collection_name}' and product '${document?.product_name}' (recoverable)`
          }).subscribe({
            next: () => this.auditLogPanel?.reloadLogs(),
            error: () => this.auditLogPanel?.reloadLogs()
          });

          this.toasterService.success('Document archived. You can restore it anytime from the Archived view.');
          this.fetchDocuments();
        });
      }
    });
  }

  handleRestoreDocument(id: string) {
    const document = this.documentData().find(d => String(d.id) === String(id));
    const safeName = (document?.file_name || 'this document').replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    this.dialog.open(ConfirmationModalComponent, {
      width: '440px',
      data: {
        title: 'Restore document',
        description: `Restore "${document?.file_name || 'this document'}" to active use?`,
        descriptionHtml: `Restore <strong>${safeName}</strong> to active use? It will reappear in the active document list and become available to search and queries again.`,
        confirmLabel: 'Restore',
        cancelLabel: 'Cancel',
        variant: 'primary'
      } as ConfirmationModalData
    }).afterClosed().subscribe(res => {
      if (res) {
        this.apiservice.post(this.baseURL + `/documents/${id}/restore`, {}, { withCredentials: true }).subscribe({
          next: () => {
            const docProduct = this.allProducts().find(p => p.product_name === document?.product_name || p.name === document?.product_name);
            const docProductKey = docProduct?.keyname;
            this.auditService.createAuditLog({
              entityType: AuditEntityType.KNOWLEDGE_BASE,
              action: AuditAction.RESTORE,
              actionTitle: `Knowledge Base Document Restored`,
              oldValue: null,
              newValue: document,
              productKey: docProductKey,
              note: `Restored document '${document?.file_name}' to collection '${document?.collection_name}' and product '${document?.product_name}'`
            }).subscribe({
              next: () => this.auditLogPanel?.reloadLogs(),
              error: () => this.auditLogPanel?.reloadLogs()
            });

            this.toasterService.success('Document restored to active use.');
            this.fetchDocuments();
          },
          error: (err: any) => {
            const msg = err?.error?.message || err?.message || 'Failed to restore document.';
            this.toasterService.error(msg, 'Restore', 3000);
          }
        });
      }
    });
  }

  // Shared Logic
  selectedRowItem(item: any) {
    if (!item || !item.row) return;
    if (item.action === 'delete') {
      this.handleRemoveCollection(item.row.id);
    }
  }

  handleTableAction(event: any) {
    if (event.action === 'view') this.handleViewDocument(event.row);
    if (event.action === 'download') this.handleDownload(event.row);
    if (event.action === 'archive') this.handleArchiveDocument(event.row.id);
    if (event.action === 'restore') this.handleRestoreDocument(event.row.id);
  }

  handleRemoveCollection(id: string) {
    const collection = this.collectionData().find(c => String(c.id) === String(id));
    this.dialog.open(ConfirmationModalComponent, {
      width: '400px',
      data: { title: 'Confirm Deletion', description: 'Are you sure you want to delete this collection?' }
    }).afterClosed().subscribe(res => {
      if (res) {
        this.apiservice.delete(this.baseURL + `/collections/${id}`, { withCredentials: true }).subscribe(() => {
          const deletedProduct = this.allProducts().find(p => String(p.id) === String(collection?.product_id));
          const deletedProductKey = deletedProduct?.keyname;
          this.auditService.createAuditLog({
            entityType: AuditEntityType.KNOWLEDGE_BASE,
            action: AuditAction.DELETE,
            actionTitle: `Knowledge Base Collection Deleted`,
            oldValue: collection,
            newValue: null,
            productKey: deletedProductKey,
            note: `Deleted collection '${collection?.name}' and product '${deletedProduct?.name}'`
          }).subscribe();

          this.toasterService.success('Collection removed successfully');
          this.fetchCollections();
          this.auditLogPanel?.reloadLogs();
        });
      }
    });
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = controlName.includes('.') ? this.fb.group({}).get(controlName) : (this.form.get(controlName) || this.documentForm.get(controlName));
    if (!control || ((!control.touched && !this.formSubmitted()) || control.valid)) return undefined;
    if (control.hasError('required')) return 'This field is required';
    return undefined;
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toasterService.success('Copied to clipboard');
    });
  }

  openAddUserPanel() { this.openPanel(); }

  // Query Panel Methods
  private _shouldScrollChat = false;

  ngAfterViewChecked() {
    if (this._shouldScrollChat && this.chatScrollArea?.nativeElement) {
      this._shouldScrollChat = false;
      const el = this.chatScrollArea.nativeElement;
      el.scrollTop = el.scrollHeight;
      // Markdown / source chips render asynchronously and grow the scroll height after this
      // change-detection pass, so re-pin to the bottom on the next frames as content settles.
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
      setTimeout(() => { el.scrollTop = el.scrollHeight; }, 120);
    }
  }

  openQueryPanel() {
    this.queryText.set('');
    this.querySubmitted.set(false);
    this.chatHistory.set([]);
    // Pre-select the currently selected collection if available
    const selected = this.selectedCollection();
    if (selected) {
      const matchingOption = this.collectionOptions().find(o => o.value === String(selected.id));
      this.queryCollectionName.set(matchingOption ? String(matchingOption.value) : '');
      this.isQueryCollectionFrozen.set(true);
    } else {
      this.queryCollectionName.set('');
      this.isQueryCollectionFrozen.set(false);
    }
    this.queryPanelOpen.set(true);
  }

  closeQueryPanel() {
    this.queryPanelOpen.set(false);
    this.queryText.set('');
    this.chatHistory.set([]);
    this.querySubmitted.set(false);
  }

  onQueryCollectionChange(colId: any) {
    this.queryCollectionName.set(colId);
    this.chatHistory.set([]);
  }

  sendSuggestion(text: string) {
    this.queryText.set(text);
    this.submitQuery();
  }

  private getCurrentTime(): string {
    const now = new Date();
    return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }

  submitQuery() {
    this.querySubmitted.set(true);
    if (!this.queryText() || !this.queryCollectionName() || this.isQueryLoading()) return;

    const userMsg: ChatMessage = { role: 'user', text: this.queryText(), time: this.getCurrentTime(), timestamp: new Date() };
    const loadingMsg: ChatMessage = { role: 'ai', text: '', time: this.getCurrentTime(), timestamp: new Date(), isLoading: true };

    this.chatHistory.update(h => [...h, userMsg, loadingMsg]);
    this._shouldScrollChat = true;
    const querySnapshot = this.queryText();
    this.queryText.set('');
    this.isQueryLoading.set(true);

    // Resolve collection_name string from the selected collection id
    const col = this.collectionData().find(c => String(c.id) === String(this.queryCollectionName()));
    const collectionNameStr = col?.collection_name || this.queryCollectionName();

    const payload = {
      query: querySnapshot,
      collection_name: collectionNameStr
    };

    this.apiservice.post<any>(
      this.baseURL + '/query',
      payload,
      { withCredentials: true }
    ).subscribe({
      next: (response: any) => {
        this.isQueryLoading.set(false);
        // Try every possible shape the interceptor might wrap this in
        const raw = response?.data ?? response;
        const dataItems: any[] = Array.isArray(raw) ? raw
          : Array.isArray(raw?.data) ? raw.data
            : [];
        const textChunks = dataItems.map((item: any) => item?.text || '').filter(Boolean);
        const answer = textChunks.length > 0
          ? textChunks.join('\n\n---\n\n')
          : (response?.answer || response?.result || response?.response || raw?.answer || 'No results found.');
        const sources = dataItems.reduce((acc: any[], item: any) => {
          if (item?.document_id && !acc.find(s => s.id === String(item.document_id))) {
            acc.push({
              id: String(item.document_id),
              name: item.document_name || 'Unknown Document'
            });
          }
          return acc;
        }, []);

        // Replace loading bubble with actual response
        this.chatHistory.update(h => [
          ...h.slice(0, -1),
          { role: 'ai', text: answer, time: this.getCurrentTime(), timestamp: new Date(), sources: sources.length > 0 ? sources : undefined }
        ]);
        this._shouldScrollChat = true;
      },
      error: (err: any) => {
        this.isQueryLoading.set(false);
        const errMsg = err?.error?.message || err?.message || 'Failed to query knowledge base.';
        this.chatHistory.update(h => [
          ...h.slice(0, -1),
          { role: 'ai', text: `⚠️ ${errMsg}`, time: this.getCurrentTime(), timestamp: new Date() }
        ]);
        this._shouldScrollChat = true;
      }
    });
  }

  private envLabel(forEnv: number | string | undefined): string {
    return this.envOptions.find(o => o.value === String(forEnv))?.label ?? '—';
  }

  /** Env selector changed: confirm, then PUT `for_env`; cancel snaps the selector back. */
  requestEnvChange(value: string): void {
    const collection = this.selectedCollection();
    const target = Number(value);
    if (!collection || !target || target === collection.for_env) return;
    this.envDraft.set(value);
    const ref = this.dialog.open(ConfirmationModalComponent, {
      width: '420px',
      panelClass: 'confirmation-dialog-panel',
      data: {
        title: `Move to ${this.envLabel(target)} Environment`,
        description: `Move the collection "${collection.name}" from ${this.envLabel(collection.for_env)} to ${this.envLabel(target)}?`,
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        variant: 'primary'
      } as ConfirmationModalData
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.moveCollectionToEnvironment(collection, target);
      } else {
        this.envDraft.set(String(collection.for_env ?? ''));
      }
    });
  }

  private moveCollectionToEnvironment(collection: Collection, target: number): void {
    const collectionId = collection.id;
    this.loaderService.show();
    this.apiservice.put(
      `${this.baseURL}/collections/${collectionId}/for-env`,
      { for_env: target },
      { withCredentials: true }
    ).subscribe({
      next: () => {
        this.loaderService.hide();
        const movedProduct = this.allProducts().find(p => String(p.id) === String(collection.product_id));
        const movedProductKey = movedProduct?.keyname;
        this.auditService.createAuditLog({
          entityType: AuditEntityType.KNOWLEDGE_BASE,
          action: AuditAction.UPDATE,
          actionTitle: `Knowledge Base Collection Moved Environment`,
          oldValue: { id: collectionId, collection_name: collection.collection_name, for_env: collection.for_env, env: this.envLabel(collection.for_env) },
          newValue: { id: collectionId, collection_name: collection.collection_name, for_env: target, env: this.envLabel(target) },
          productKey: movedProductKey,
          note: `Moved collection '${collection.name}' and product '${movedProduct?.name}' to ${this.envLabel(target)} environment`
        }).subscribe();

        this.toasterService.success(`Collection moved to ${this.envLabel(target)} environment.`);
        this.documentData.set([]);
        this.totalDocuments.set(0);
        this.embeddedDocuments.set(0);
        this.failedDocuments.set(0);
        this.fetchCollections();
        this.fetchDocuments();
        this.auditLogPanel?.reloadLogs();
      },
      error: (err: any) => {
        this.loaderService.hide();
        this.envDraft.set(String(collection.for_env ?? ''));
        const msg = err?.error?.message || err?.message || 'Failed to move collection.';
        this.toasterService.error(msg, 'Move environment', 3000);
      }
    });
  }
}
